import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, Eye, Search, FileText, X, Download, Shield } from 'lucide-react';
import apiClient from '../api/client';
import { ProductApplicationDto, PolicyLinkRequestSummaryDto, PolicyLinkRequestDetailDto } from '../types/dtos';
import LoadingSpinner from '../components/LoadingSpinner';
import FeedbackDialog, { FeedbackType } from '../components/FeedbackDialog';
import AssignProductModal from '../components/AssignProductModal';
import { resolveUrl } from '../utils/urlUtils';
import toast from 'react-hot-toast';

export default function Applications() {
    const [applications, setApplications] = useState<ProductApplicationDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState('Pending Approval');
    const [selectedApp, setSelectedApp] = useState<ProductApplicationDto | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [rejectionNotes, setRejectionNotes] = useState('');
    const [showRejectPrompt, setShowRejectPrompt] = useState(false);
    const [applicationsTab, setApplicationsTab] = useState<'New' | 'Existing' | 'History'>('New');
    const [historyApps, setHistoryApps] = useState<ProductApplicationDto[]>([]);
    const [historyRequests, setHistoryRequests] = useState<PolicyLinkRequestSummaryDto[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('All');

    // Policy Request specific state
    const [requests, setRequests] = useState<PolicyLinkRequestSummaryDto[]>([]);
    const [requestsLoading, setRequestsLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState<PolicyLinkRequestDetailDto | null>(null);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [isAppAssignModalOpen, setIsAppAssignModalOpen] = useState(false);
    const [assignModalStatus, setAssignModalStatus] = useState<'Active' | 'Quote Ready'>('Active');
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');

    const [feedback, setFeedback] = useState<{
        isOpen: boolean;
        type: FeedbackType;
        title: string;
        message: string;
        onPrimaryAction?: () => void;
    }>({ isOpen: false, type: 'success', title: '', message: '' });

    const fetchApplications = async () => {
        setLoading(true);
        try {
            const res = await apiClient.get<ProductApplicationDto[]>('/admin/applications');
            setApplications(res.data);
        } catch (err: any) {
            console.error(err);
            setError('Failed to load applications.');
        } finally {
            setLoading(false);
        }
    };

    const fetchRequests = async () => {
        setRequestsLoading(true);
        try {
            const res = await apiClient.get<PolicyLinkRequestSummaryDto[]>('/admin/policy-requests');
            setRequests(res.data);
        } catch (err) {
            console.error('Failed to fetch requests', err);
            setError('Failed to load policy requests.');
        } finally {
            setRequestsLoading(false);
        }
    };

    const fetchHistory = async () => {
        setHistoryLoading(true);
        try {
            const [appsRes, reqsRes] = await Promise.all([
                apiClient.get<ProductApplicationDto[]>('/admin/applications?showAll=true'),
                apiClient.get<PolicyLinkRequestSummaryDto[]>('/admin/policy-requests?showAll=true')
            ]);
            // Filter to only show "processed" items in history, OR show everything?
            // User said "all applications with their status", so showing everything is fine.
            // But usually history means "non-pending". 
            // Let's show everything in history so they can see context.
            setHistoryApps(appsRes.data);
            setHistoryRequests(reqsRes.data);
        } catch (err) {
            console.error('Failed to fetch history', err);
            setError('Failed to load history.');
        } finally {
            setHistoryLoading(false);
        }
    };

    useEffect(() => {
        if (applicationsTab === 'History') {
            fetchHistory();
        } else {
            fetchApplications();
            fetchRequests();
        }
    }, [applicationsTab]);

    const handleStatusUpdate = async (id: string, status: string, notes?: string) => {
        try {
            await apiClient.put(`/admin/applications/${id}/status`, {
                status,
                notes
            });
            setApplications(prev => prev.filter(app => app.id !== id));
            if (applicationsTab === 'History') fetchHistory();
            closeModal();
            const getSuccessMessage = () => {
                if (status === 'Active') return 'Application has been Approved and Activated.';
                if (status === 'Rejected') return 'Application has been Rejected.';
                if (status === 'Quote Ready') return 'Quote has been successfully sent to the customer.';
                return `Application status updated to ${status}.`;
            };

            setFeedback({
                isOpen: true,
                type: 'success',
                title: 'Success',
                message: getSuccessMessage()
            });
        } catch (error) {
            console.error(error);
            setFeedback({
                isOpen: true,
                type: 'error',
                title: 'Error',
                message: 'Failed to update application status.'
            });
        }
    };

    const closeModal = () => {
        setSelectedApp(null);
        setShowRejectPrompt(false);
        setRejectionNotes('');
        setIsAppAssignModalOpen(false);
    };

    const fetchRequestDetails = async (id: string) => {
        try {
            const res = await apiClient.get<PolicyLinkRequestDetailDto>(`/admin/policy-requests/${id}`);
            setSelectedRequest(res.data);
        } catch (err) {
            console.error('Failed to fetch request details', err);
            toast.error('Failed to load request details.');
        }
    };

    const handleAssignSuccess = async () => {
        if (!selectedRequest) return;
        try {
            await apiClient.put(`/admin/policy-requests/${selectedRequest.id}/status`, { status: 'Approved' });
            setIsAssignModalOpen(false);
            setSelectedRequest(null);
            if (applicationsTab === 'History') {
                fetchHistory();
            } else {
                fetchRequests();
            }
            setFeedback({ isOpen: true, type: 'success', title: 'Success', message: 'Policy successfully linked.' });
        } catch (err) {
            console.error('Failed to update request status', err);
            toast.error('Assigned product, but failed to mark request as Approved.');
        }
    };

    const handleRejectSubmit = async () => {
        if (!selectedRequest) return;
        try {
            await apiClient.put(`/admin/policy-requests/${selectedRequest.id}/status`, {
                status: 'Rejected',
                reason: rejectReason
            });
            setIsRejectModalOpen(false);
            setRejectReason('');
            setSelectedRequest(null);
            if (applicationsTab === 'History') {
                fetchHistory();
            } else {
                fetchRequests();
            }
            setFeedback({ isOpen: true, type: 'success', title: 'Success', message: 'Request rejected successfully.' });
        } catch (err) {
            console.error('Failed to reject request', err);
            toast.error('Failed to reject request.');
        }
    };

    const getStatusStyle = (status: string) => {
        switch (status) {
            case 'Pending': return 'bg-yellow-100 text-yellow-800';
            case 'Approved':
            case 'Active': return 'bg-green-100 text-green-800';
            case 'Rejected': return 'bg-red-100 text-red-800';
            case 'Quote Ready': return 'bg-blue-100 text-blue-800';
            case 'Payment Pending': return 'bg-purple-100 text-purple-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'Pending': return <Clock size={16} />;
            case 'Approved':
            case 'Active': return <CheckCircle size={16} />;
            case 'Rejected': return <XCircle size={16} />;
            case 'Quote Ready': return <FileText size={16} />;
            default: return null;
        }
    };

    const newApplications = applications.filter(app => {
        const type = (app.applicationType || 'new').toLowerCase();
        return type !== 'existing';
    });

    const filteredApps = newApplications.filter(app => {
        const matchesSearch = app.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            app.instanceId.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
    });

    const filteredRequests = requests.filter(req => {
        const matchesSearch = req.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            req.policyNumber.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
    });

    const renderQuestions = (detailsJson: string | null | undefined, questionsJson: string | null | undefined) => {
        if (!detailsJson) return null;
        try {
            const details = JSON.parse(detailsJson);
            const answers = details.questionAnswers || (detailsJson.includes('"questionAnswers"') ? {} : details); // Support flat objects
            if (Object.keys(answers).length === 0) return null;

            // map keys to labels (Robust mapping: id -> label AND name -> label)
            let questionsMap: Record<string, string> = {};
            if (questionsJson) {
                try {
                    const parsedQuestions = JSON.parse(questionsJson);
                    if (Array.isArray(parsedQuestions)) {
                        parsedQuestions.forEach((q: any) => {
                            if (q.id) questionsMap[q.id] = q.label;
                            if (q.name) questionsMap[q.name] = q.label;
                        });
                    }
                } catch (e) { console.error("Error parsing questionsJson", e); }
            }

            return (
                <div className="space-y-3">
                    {Object.entries(answers).map(([key, value]) => {
                        if (key === 'documents' || key === 'questionAnswers') return null;
                        return (
                            <div key={key} className="flex flex-col p-3 bg-white border rounded-lg">
                                <span className="text-xs font-bold text-gray-500 uppercase mb-1">
                                    {questionsMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')}
                                </span>
                                <span className="text-sm font-medium text-gray-900">
                                    {Array.isArray(value) ? value.join(', ') : String(value)}
                                </span>
                            </div>
                        );
                    })}
                </div>
            );
        } catch (e) {
            return null;
        }
    };

    const renderDocuments = (app: ProductApplicationDto) => {
        const detailsJson = app.detailsJson;
        if (!detailsJson) return <p className="text-gray-500 italic text-sm">No documents provided.</p>;
        try {
            const details = JSON.parse(detailsJson);
            const docs = details.documents || {};
            if (Object.keys(docs).length === 0) return <p className="text-gray-500 italic text-sm">No documents provided.</p>;

            // Parse onboarding config to find human-readable names for document keys
            let onboardingConfig: any = null;
            try {
                if (app.onboardingConfigurationJson) {
                    onboardingConfig = JSON.parse(app.onboardingConfigurationJson);
                }
            } catch (e) {}

            return (
                <div className="grid grid-cols-1 gap-3">
                    {Object.entries(docs).map(([key, docUrl]: [string, any]) => {
                        // Handle legacy object format or new string URL format
                        const rawUrl = typeof docUrl === 'string' ? docUrl : (docUrl?.url || docUrl?.base64);
                        if (!rawUrl) return null;

                        const url = resolveUrl(rawUrl);
                        const isImage = url.match(/\.(jpg|jpeg|png|gif|webp)$/i) || url.startsWith('data:image');
                        
                        // Find descriptive name from config
                        let label = key;
                        if (onboardingConfig?.requiredDocuments) {
                            const docDef = onboardingConfig.requiredDocuments.find((d: any) => d.id === key || d.name === key || key.startsWith(d.id));
                            if (docDef) label = docDef.name;
                        }

                        return (
                            <div key={key} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl hover:border-blue-300 transition-all shadow-sm">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                                        {isImage ? <Shield size={18} /> : <FileText size={18} />}
                                    </div>
                                    <div className="max-w-[200px]">
                                        <div className="text-xs font-bold text-gray-900 truncate" title={label}>{label}</div>
                                        <div className="text-[10px] text-gray-400 font-mono truncate">{key}</div>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <a
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="px-3 py-1.5 bg-blue-900 text-white rounded-lg text-[10px] font-bold hover:bg-blue-800 transition shadow-sm flex items-center gap-1"
                                    >
                                        <Download size={12} /> DOWNLOAD
                                    </a>
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        } catch (e) {
            return <p className="text-red-500 text-sm italic">Error parsing document data.</p>;
        }
    };

    return (
        <div className="w-full">
            <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Policy Applications</h1>
                            <p className="text-sm text-gray-500">Review and approve policy applications and linking requests.</p>
                        </div>
                        <div className="flex items-center gap-3 w-full md:w-auto">
                            <div className="relative flex-1 md:w-64">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="text"
                                    placeholder="Search by name or ID..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 border rounded-lg text-sm w-full focus:ring-2 focus:ring-blue-900 outline-none"
                                />
                            </div>
                            {applicationsTab === 'History' && (
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="px-4 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-900 outline-none"
                                >
                                    <option value="All">All Statuses</option>
                                    <option value="Submitted">Submitted</option>
                                    <option value="Pending Approval">Pending Approval</option>
                                    <option value="Under Review">Under Review</option>
                                    <option value="Quote Ready">Quote Ready</option>
                                    <option value="Payment Pending">Payment Pending</option>
                                    <option value="Active">Active</option>
                                    <option value="Approved">Approved</option>
                                    <option value="Rejected">Rejected</option>
                                    <option value="Pending">Pending (Linking)</option>
                                </select>
                            )}
                        </div>
                    </div>

                    <div className="flex gap-2 mb-6 border-b border-gray-200">
                        <button
                            onClick={() => setApplicationsTab('New')}
                            className={`px-4 py-2 text-sm font-bold transition-all border-b-2 flex items-center gap-2 ${applicationsTab === 'New' ? 'border-blue-900 text-blue-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            New Product Applications
                            <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-[10px]">
                                {newApplications.length}
                            </span>
                        </button>
                        <button
                            onClick={() => setApplicationsTab('Existing')}
                            className={`px-4 py-2 text-sm font-bold transition-all border-b-2 flex items-center gap-2 ${applicationsTab === 'Existing' ? 'border-blue-900 text-blue-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            Existing Product Linking
                            <span className="bg-blue-100 text-blue-900 px-2 py-0.5 rounded-full text-[10px]">
                                {requests.length}
                            </span>
                        </button>
                        <button
                            onClick={() => setApplicationsTab('History')}
                            className={`px-4 py-2 text-sm font-bold transition-all border-b-2 flex items-center gap-2 ${applicationsTab === 'History' ? 'border-blue-900 text-blue-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            Application History
                            <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-[10px]">
                                {historyApps.length + historyRequests.length}
                            </span>
                        </button>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        {error && (
                            <div className="p-4 bg-red-50 text-red-700 border-b border-red-100 text-sm font-medium flex items-center gap-2">
                                <XCircle size={16} />
                                {error}
                                <button onClick={() => fetchApplications()} className="ml-auto underline decoration-dotted">Retry</button>
                            </div>
                        )}
                        {(applicationsTab === 'New' ? loading : applicationsTab === 'Existing' ? requestsLoading : historyLoading) ? (
                            <div className="p-12 flex justify-center"><LoadingSpinner /></div>
                        ) : (applicationsTab === 'New' ? filteredApps.length : applicationsTab === 'Existing' ? filteredRequests.length : (historyApps.length + historyRequests.length)) === 0 ? (
                            <div className="p-12 text-center text-gray-500">
                                {searchTerm ? 'No results found matching your search.' : `No ${applicationsTab === 'New' ? 'pending new product applications' : applicationsTab === 'Existing' ? 'pending existing product linking requests' : 'application history'} found.`}
                            </div>
                        ) : (
                            <div className="bg-white">
                                {/* Desktop Table */}
                                <div className="hidden md:block overflow-x-auto">
                                    <table className="w-full text-sm text-left">
                                        <thead className="bg-gray-50 text-gray-600 border-b">
                                            <tr>
                                                <th className="px-6 py-4">{applicationsTab === 'Existing' ? 'Policy Number' : 'Reference ID'}</th>
                                                <th className="px-6 py-4">Customer</th>
                                                <th className="px-6 py-4">Type</th>
                                                <th className="px-6 py-4">Date</th>
                                                <th className="px-6 py-4">Status</th>
                                                <th className="px-6 py-4 text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {/* Render based on tab */}
                                            {applicationsTab === 'New' && filteredApps.map((app) => (
                                                <tr key={app.id} className="hover:bg-gray-50 transition">
                                                    <td className="px-6 py-4 font-mono text-xs">{app.instanceId}</td>
                                                    <td className="px-6 py-4 font-bold">{app.customerName}</td>
                                                    <td className="px-6 py-4 text-blue-900 font-medium">{app.productName}</td>
                                                    <td className="px-6 py-4 text-gray-500">{new Date(app.createdAt).toLocaleDateString()}</td>
                                                    <td className="px-6 py-4">
                                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold leading-none ${getStatusStyle(app.status)}`}>
                                                            {getStatusIcon(app.status)}
                                                            {app.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <button
                                                            onClick={() => setSelectedApp(app)}
                                                            className="bg-blue-900 text-white px-4 py-1.5 rounded-lg text-xs font-bold hover:bg-blue-800 transition"
                                                        >
                                                            Review
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {applicationsTab === 'Existing' && filteredRequests.map((req) => (
                                                <tr key={req.id} className="hover:bg-gray-50 transition">
                                                    <td className="px-6 py-4 font-mono text-xs">{req.policyNumber}</td>
                                                    <td className="px-6 py-4 font-bold">{req.customerName}</td>
                                                    <td className="px-6 py-4">Linking Request</td>
                                                    <td className="px-6 py-4 text-gray-500">{new Date(req.createdAt).toLocaleDateString()}</td>
                                                    <td className="px-6 py-4">
                                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold leading-none ${getStatusStyle(req.status)}`}>
                                                            {getStatusIcon(req.status)}
                                                            {req.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <button
                                                            onClick={() => fetchRequestDetails(req.id)}
                                                            className="bg-blue-900 text-white px-4 py-1.5 rounded-lg text-xs font-bold hover:bg-blue-800 transition"
                                                        >
                                                            Review
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {applicationsTab === 'History' && [...historyApps, ...historyRequests.map(r => ({ ...r, isLinking: true }))]
                                                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                                                .filter(item => {
                                                    const search = searchTerm.toLowerCase();
                                                    const matchesSearch = (item.customerName?.toLowerCase().includes(search) || false) ||
                                                        ((item as any).instanceId || (item as any).policyNumber || "").toLowerCase().includes(search);
                                                    const matchesStatus = statusFilter === 'All' || item.status === statusFilter;
                                                    return matchesSearch && matchesStatus;
                                                })
                                                .map((item: any) => (
                                                    <tr key={item.id} className="hover:bg-gray-50 transition">
                                                        <td className="px-6 py-4 font-mono text-xs">{item.instanceId || item.policyNumber}</td>
                                                        <td className="px-6 py-4 font-bold">{item.customerName}</td>
                                                        <td className="px-6 py-4 text-gray-500 text-xs">
                                                            {item.isLinking ? 'Linking Request' : item.productName}
                                                        </td>
                                                        <td className="px-6 py-4 text-gray-500">{new Date(item.createdAt).toLocaleDateString()}</td>
                                                        <td className="px-6 py-4">
                                                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold leading-none ${getStatusStyle(item.status)}`}>
                                                                {getStatusIcon(item.status)}
                                                                {item.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-right">
                                                            <button
                                                                onClick={() => item.isLinking ? fetchRequestDetails(item.id) : setSelectedApp(item)}
                                                                className="text-blue-900 font-bold hover:underline text-xs"
                                                            >
                                                                View Details
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile Card View */}
                                <div className="md:hidden divide-y divide-gray-100">
                                    {applicationsTab === 'New' && filteredApps.map((app) => (
                                        <div key={app.id} className="p-4 bg-white hover:bg-gray-50 transition-colors">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase">ID: {app.instanceId}</p>
                                                    <h3 className="font-bold text-gray-900">{app.customerName}</h3>
                                                </div>
                                                <button
                                                    onClick={() => setSelectedApp(app)}
                                                    className="bg-blue-900 text-white px-3 py-1 rounded-lg text-xs font-bold"
                                                >
                                                    Review
                                                </button>
                                            </div>
                                            <div className="flex justify-between items-end mt-3">
                                                <div>
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase">Product</p>
                                                    <p className="text-sm text-blue-900 font-medium">{app.productName}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase">Submitted</p>
                                                    <p className="text-xs text-gray-500">{new Date(app.createdAt).toLocaleDateString()}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {applicationsTab === 'Existing' && filteredRequests.map((req) => (
                                        <div key={req.id} className="p-4 bg-white hover:bg-gray-50 transition-colors">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase">Policy: {req.policyNumber}</p>
                                                    <h3 className="font-bold text-gray-900">{req.customerName}</h3>
                                                </div>
                                                <button
                                                    onClick={() => fetchRequestDetails(req.id)}
                                                    className="bg-blue-900 text-white px-3 py-1 rounded-lg text-xs font-bold"
                                                >
                                                    Review
                                                </button>
                                            </div>
                                            <div className="flex justify-between items-end mt-3">
                                                <div>
                                                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold leading-none ${getStatusStyle(req.status)}`}>
                                                        {req.status}
                                                    </span>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase">Submitted</p>
                                                    <p className="text-xs text-gray-500">{new Date(req.createdAt).toLocaleDateString()}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {applicationsTab === 'History' && [...historyApps, ...historyRequests.map(r => ({ ...r, isLinking: true }))]
                                        .filter(item => {
                                            const search = searchTerm.toLowerCase();
                                            const matchesSearch = (item.customerName?.toLowerCase().includes(search) || false) ||
                                                ((item as any).instanceId || (item as any).policyNumber || "").toLowerCase().includes(search);
                                            const matchesStatus = statusFilter === 'All' || item.status === statusFilter;
                                            return matchesSearch && matchesStatus;
                                        })
                                        .map((item: any) => (
                                            <div key={item.id} className="p-4 bg-white hover:bg-gray-50 transition-colors">
                                                <div className="flex justify-between items-start mb-2">
                                                    <div>
                                                        <p className="text-[10px] text-gray-400 font-bold uppercase">{item.isLinking ? 'Policy' : 'ID'}: {item.instanceId || item.policyNumber}</p>
                                                        <h3 className="font-bold text-gray-900">{item.customerName}</h3>
                                                    </div>
                                                    <button
                                                        onClick={() => item.isLinking ? fetchRequestDetails(item.id) : setSelectedApp(item)}
                                                        className="bg-gray-100 text-blue-900 px-3 py-1 rounded-lg text-xs font-bold"
                                                    >
                                                        Details
                                                    </button>
                                                </div>
                                                <div className="flex justify-between items-end mt-3">
                                                    <div>
                                                        <p className="text-[10px] text-gray-400 font-bold uppercase">Status</p>
                                                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold leading-none ${getStatusStyle(item.status)}`}>
                                                            {item.status}
                                                        </span>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-[10px] text-gray-400 font-bold uppercase">{item.isLinking ? 'Linking' : item.productName}</p>
                                                        <p className="text-xs text-gray-500">{new Date(item.createdAt).toLocaleDateString()}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}
                    </div>
            </div>

            {/* Review Modal */}
            {selectedApp && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="p-6 bg-blue-900 text-white flex justify-between items-center">
                            <h3 className="text-xl font-bold">Review Application</h3>
                            <button onClick={() => {
                                setSelectedApp(null);
                                setShowRejectPrompt(false);
                            }}><X size={20} /></button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <div className="text-xs text-gray-500 uppercase font-bold">Customer</div>
                                    <div className="font-bold text-gray-900">{selectedApp.customerName}</div>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <div className="text-xs text-gray-500 uppercase font-bold">Policy Number</div>
                                    <div className="font-bold text-blue-900">{selectedApp.instanceId}</div>
                                </div>
                            </div>

                            <div>
                                <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                                    <FileText size={18} className="text-blue-600" />
                                    Uploaded Documents
                                </h4>
                                <div className="bg-gray-50 p-4 rounded-xl border">
                                    {renderDocuments(selectedApp)}
                                </div>
                            </div>

                            {/* Dynamic Questions Section */}
                            {(() => {
                                const questions = renderQuestions(selectedApp.detailsJson, selectedApp.questionsJson);
                                if (questions) return (
                                    <div>
                                        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                                            <FileText size={18} className="text-blue-600" />
                                            Application Details
                                        </h4>
                                        <div className="bg-gray-50 p-4 rounded-xl border">
                                            {questions}
                                        </div>
                                    </div>
                                );
                                return null;
                            })()}
                        </div>

                        <div className="p-6 bg-gray-50 border-t flex flex-col gap-4">
                            {showRejectPrompt ? (
                                <div className="space-y-3">
                                    <label className="text-xs font-bold text-gray-500 uppercase">Reason for Rejection</label>
                                    <textarea
                                        value={rejectionNotes}
                                        onChange={(e) => setRejectionNotes(e.target.value)}
                                        placeholder="Explain why the application is being rejected (e.g. Invalid documents, incomplete information)"
                                        className="w-full p-3 border rounded-lg text-sm focus:ring-2 focus:ring-red-500 outline-none"
                                        rows={3}
                                    />
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setShowRejectPrompt(false)}
                                            className="flex-1 py-2 text-sm font-bold text-gray-500 hover:bg-gray-100 rounded-lg transition"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={() => handleStatusUpdate(selectedApp.id, 'Rejected', rejectionNotes)}
                                            disabled={!rejectionNotes.trim()}
                                            className="flex-3 py-2 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Confirm Rejection
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-4">
                                    <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100 flex items-center gap-4">
                                        <div className="bg-white p-3 rounded-full shadow-sm">
                                            <Shield className="text-blue-900" size={24} />
                                        </div>
                                        <div>
                                            <div className="text-sm font-bold text-gray-900">Ready for Assignment</div>
                                            <div className="text-xs text-gray-500">Review the application details above, then click below to assign a policy ID and activate.</div>
                                        </div>
                                    </div>

                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => setShowRejectPrompt(true)}
                                            className="flex-1 py-3 border border-red-200 text-red-700 bg-white rounded-lg font-bold hover:bg-red-50 flex items-center justify-center gap-2"
                                        >
                                            <XCircle size={18} /> Reject
                                        </button>
                                        <button
                                            onClick={() => {
                                                setAssignModalStatus('Quote Ready');
                                                setIsAppAssignModalOpen(true);
                                            }}
                                            className="flex-1 py-3 border border-blue-200 text-blue-900 bg-white rounded-lg font-bold hover:bg-blue-50 flex items-center justify-center gap-2"
                                        >
                                            <FileText size={18} /> Send Quote
                                        </button>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setAssignModalStatus('Active');
                                            setIsAppAssignModalOpen(true);
                                        }}
                                        className="w-full py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 shadow-md flex items-center justify-center gap-2"
                                    >
                                        <CheckCircle size={18} /> Approve & Activate (Instant)
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Request Details Modal */}
            {selectedRequest && !isAssignModalOpen && !isRejectModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">Request Details</h2>
                                <p className="text-sm text-gray-500">Submitted by {selectedRequest.customerName}</p>
                            </div>
                            <button onClick={() => setSelectedRequest(null)} className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-400">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-gray-50 p-4 rounded-xl">
                                    <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Policy Number</p>
                                    <p className="font-mono text-gray-900 font-medium">{selectedRequest.policyNumber}</p>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-xl">
                                    <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Status</p>
                                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold leading-none ${getStatusStyle(selectedRequest.status)}`}>
                                        {getStatusIcon(selectedRequest.status)}
                                        {selectedRequest.status}
                                    </span>
                                </div>
                            </div>

                            {selectedRequest.rejectionReason && (
                                <div className="bg-red-50 text-red-800 p-4 rounded-xl border border-red-100">
                                    <p className="text-xs text-red-600 uppercase font-bold tracking-wider mb-1">Rejection Reason</p>
                                    <p className="text-sm">{selectedRequest.rejectionReason}</p>
                                </div>
                            )}

                            <div>
                                <h3 className="text-sm font-bold text-gray-900 border-b pb-2 mb-4">Supporting Documents</h3>
                                {selectedRequest.documentUrls && selectedRequest.documentUrls.length > 0 ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {selectedRequest.documentUrls.map((url: string, idx: number) => {
                                            const filename = url.split('/').pop() || `Document ${idx + 1}`;
                                            const resolvedUrl = resolveUrl(url);
                                            return (
                                                <a
                                                    key={idx}
                                                    href={resolvedUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="flex items-center gap-3 p-3 bg-blue-50/50 hover:bg-blue-50 border border-blue-100 rounded-xl transition-colors group"
                                                >
                                                    <FileText className="text-blue-500 group-hover:text-blue-600 flex-shrink-0" size={24} />
                                                    <span className="text-sm font-medium text-blue-900 truncate">{filename}</span>
                                                </a>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 text-sm">No documents were uploaded.</p>
                                )}
                            </div>
                        </div>

                        {selectedRequest.status === 'Pending' && (
                            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                                <button
                                    onClick={() => setIsRejectModalOpen(true)}
                                    className="px-6 py-2 rounded-xl text-red-600 font-bold hover:bg-red-50 border border-transparent hover:border-red-100 transition-colors"
                                >
                                    Reject
                                </button>
                                <button
                                    onClick={() => setIsAssignModalOpen(true)}
                                    className="px-8 py-2 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/20 active:scale-95 transition-all"
                                >
                                    Approve & Assign Product
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Reject Modal */}
            {isRejectModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-red-50">
                            <h2 className="text-xl font-bold text-red-800 flex items-center gap-2">
                                <XCircle size={24} /> Reject Request
                            </h2>
                            <button onClick={() => setIsRejectModalOpen(false)} className="p-2 hover:bg-red-100 rounded-full transition-colors text-red-400">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-6">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Reason for Rejection *</label>
                            <textarea
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                className="w-full border border-gray-200 rounded-xl p-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all outline-none"
                                rows={4}
                                placeholder="Please explain why this request is being rejected..."
                            ></textarea>
                            <p className="text-xs text-gray-500 mt-2">This reason will be visible to the customer.</p>
                        </div>
                        <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 border-t border-gray-100">
                            <button onClick={() => setIsRejectModalOpen(false)} className="px-5 py-2 text-gray-600 font-bold hover:bg-gray-200 rounded-xl">Cancel</button>
                            <button
                                onClick={handleRejectSubmit}
                                disabled={!rejectReason.trim()}
                                className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-red-600/20 active:scale-95 transition-all"
                            >
                                Confirm Rejection
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Assign Product Modal */}
            {selectedRequest && isAssignModalOpen && (
                <AssignProductModal
                    isOpen={isAssignModalOpen}
                    onClose={() => setIsAssignModalOpen(false)}
                    onSuccess={handleAssignSuccess}
                    customerId={selectedRequest.customerUserId}
                    customerName={selectedRequest.customerName}
                />
            )}

            <FeedbackDialog
                isOpen={feedback.isOpen}
                onClose={() => setFeedback(prev => ({ ...prev, isOpen: false }))}
                type={feedback.type}
                title={feedback.title}
                message={feedback.message}
            />
            {/* Assign Modal for New Applications */}
            {selectedApp && isAppAssignModalOpen && (
                <AssignProductModal
                    isOpen={isAppAssignModalOpen}
                    onClose={() => setIsAppAssignModalOpen(false)}
                    onSuccess={() => {
                        setApplications(prev => prev.filter(app => app.id !== selectedApp.id));
                        if (applicationsTab === 'History') fetchHistory();
                        closeModal();
                        setFeedback({
                            isOpen: true,
                            type: 'success',
                            title: 'Success',
                            message: assignModalStatus === 'Active' ? 'Application processed and product assigned.' : 'Quote generated successfully.'
                        });
                    }}
                    customerId="" // Not strictly needed when applicationId is provided
                    customerName={selectedApp.customerName}
                    applicationId={selectedApp.id}
                    preselectedProductId={selectedApp.productId}
                    preselectedTierId={selectedApp.tierId}
                    initialStatus={assignModalStatus}
                />
            )}
        </div>
    );
}
